import type { MainContestInput } from "../contest/contest-types.js";
import { RoundCommandError } from "./round-errors.js";
import { validateImportedTotals } from "../imported-round/imported-totals-validation.js";
import type {
  CourseHoleSnapshot,
  CreateRoundSnapshotInput,
  HoleEntryInput,
  RoundParticipantInput,
} from "./round-types.js";

export function validateV3Input(input: CreateRoundSnapshotInput): void {
  if (input.schemaVersion !== 3) fail("Unsupported round schema version.");
  if (input.importedTotals) {
    validateImportedTotals(input);
    if (input.scorekeeperGeneration !== 0)
      fail("A new imported result needs generation zero.");
    validateSides(input, validateContest(input, []));
    return;
  }
  const numbers = input.orderedHoleNumbers;
  if (
    !numbers ||
    numbers.length < 1 ||
    numbers.length > 18 ||
    new Set(numbers).size !== numbers.length ||
    numbers.some(
      (number) => !Number.isInteger(number) || number < 1 || number > 18,
    )
  )
    fail("Choose unique ordered physical holes.");
  const selected = numbers!;
  validateHoles(input.course.holes);
  if (
    selected.some(
      (number) => !input.course.holes.some((hole) => hole.number === number),
    )
  )
    fail("The course is missing selected physical holes.");
  if (input.scorekeeperGeneration !== 0)
    fail("A new round must start with scorekeeper generation zero.");
  const contest = validateContest(input, selected);
  validateSides(input, contest);
  for (const participant of input.participants)
    validatePlayer(
      participant,
      input.course.id,
      selected,
      contest.scoringBasis === "net",
    );
}

function validateContest(
  input: CreateRoundSnapshotInput,
  selected: number[],
): MainContestInput {
  const contest = input.mainContest;
  if (
    !contest ||
    !contest.id ||
    contest.scope !== "round" ||
    contest.rulesVersion !== 1 ||
    contest.lockedAt !== null
  )
    fail("A new round needs one unlocked, versioned main contest.");
  if (!["stroke-play", "match-play", "stableford"].includes(contest!.format))
    fail("This format is not available for new v3 rounds.");
  if (!["gross", "net"].includes(contest!.scoringBasis))
    fail("Choose gross or net before the round.");
  if (
    contest!.format !== input.rules.format ||
    (contest!.scoringBasis === "gross") !== (input.rules.handicapMode === "off")
  )
    fail("The contest and round scoring rules must agree.");
  if (input.rules.metric || input.rules.kind === "team")
    fail(
      "New v3 rounds currently require individual Stroke, Match or Stableford.",
    );
  if (
    contest!.scoringBasis === "net" &&
    input.rules.handicapMode !==
      (contest!.format === "match-play" ? "relative" : "full")
  )
    fail(
      "Net Match uses relative agreed PH; other net formats use full agreed PH.",
    );
  if (!sameNumbers(contest!.holeNumbers, selected))
    fail("The main contest must use the selected physical holes in order.");
  return contest!;
}

function validateSides(
  input: CreateRoundSnapshotInput,
  contest: MainContestInput,
): void {
  const sides = contest.sides;
  if (
    !Array.isArray(sides) ||
    sides.length < 1 ||
    sides.length > 4 ||
    new Set(sides.map((side) => side.id)).size !== sides.length
  )
    fail("Contest sides must be unique.");
  if (contest.format === "match-play" && sides.length !== 2)
    fail("Match Play needs exactly two agreed sides.");
  // Existing team formats keep their v2 reader and writer until their explicit v3 score model is available.
  if (
    sides.some(
      (side) =>
        !side.id || side.kind !== "person" || side.participantIds.length !== 1,
    )
  )
    fail("New individual contests need one player on each person side.");
  const members = sides.flatMap((side) => side.participantIds);
  if (
    new Set(members).size !== input.participants.length ||
    members.length !== input.participants.length ||
    input.participants.some(
      (participant) =>
        !members.includes(participant.id) || participant.teamId !== null,
    )
  )
    fail("Every participant must belong to exactly one agreed person side.");
}

function validatePlayer(
  participant: RoundParticipantInput,
  courseId: string,
  selected: number[],
  net: boolean,
): void {
  const tee = participant.teeSnapshot;
  const handicap = participant.handicapSnapshot;
  if (!tee || !tee.teeName?.trim() || tee.courseId !== courseId)
    fail("Capture a tee for each player on this course.");
  validateHoles(tee!.holes);
  if (
    selected.some(
      (number) => !tee!.holes.some((hole) => hole.number === number),
    )
  )
    fail("Each player's tee must contain every selected physical hole.");
  if (
    !handicap ||
    handicap.source !== "agreed" ||
    !sameNumbers(handicap.appliesToHoleNumbers, selected)
  )
    fail(
      "Playing Handicap must explicitly apply to the selected physical holes.",
    );
  const ph = handicap!.playingHandicap;
  if (ph !== null && (!Number.isInteger(ph) || ph < -20 || ph > 100))
    fail("Playing Handicap must be an agreed integer from -20 to 100.");
  if (
    handicap!.agreedAt !== null &&
    !Number.isFinite(Date.parse(handicap!.agreedAt))
  )
    fail("The handicap agreement time is invalid.");
  if (net && ph === null)
    fail("Agree a Playing Handicap for every player before starting net play.");
}

function fail(message: string): never {
  throw new RoundCommandError(message);
}

export function validateHoles(holes: CourseHoleSnapshot[]): void {
  if (
    !Array.isArray(holes) ||
    holes.length < 1 ||
    holes.length > 18 ||
    new Set(holes.map((hole) => hole.number)).size !== holes.length ||
    new Set(holes.map((hole) => hole.strokeIndex)).size !== holes.length ||
    holes.some(
      (hole) =>
        !Number.isInteger(hole.number) ||
        hole.number < 1 ||
        hole.number > 18 ||
        !Number.isInteger(hole.par) ||
        hole.par < 2 ||
        hole.par > 7 ||
        !Number.isInteger(hole.strokeIndex) ||
        hole.strokeIndex < 1 ||
        hole.strokeIndex > 18,
    )
  )
    throw new RoundCommandError(
      "Tee holes need unique physical numbers, unique stroke indexes and valid pars.",
    );
}

export function validateHoleEntry(
  entry: HoleEntryInput,
  allowPickup: boolean,
  par: number,
): void {
  const invalid = () => {
    throw new RoundCommandError(
      "The hole entry has inconsistent score or statistics.",
    );
  };
  if (
    !["unset", "scored", "pickup"].includes(entry.status) ||
    !["recorded", "import"].includes(entry.source)
  )
    invalid();
  if (entry.status === "scored") {
    if (
      !Number.isInteger(entry.strokes) ||
      entry.strokes! < 1 ||
      entry.strokes! > 30
    )
      invalid();
  } else if (entry.strokes !== null) invalid();
  if (entry.status === "pickup" && !allowPickup)
    throw new RoundCommandError("Pickup is only available in Stableford.");
  if (
    entry.putts !== null &&
    (!Number.isInteger(entry.putts) ||
      entry.putts < 0 ||
      entry.putts > 30 ||
      (entry.strokes !== null && entry.putts > entry.strokes))
  )
    invalid();
  if (entry.fairwayHit !== null && typeof entry.fairwayHit !== "boolean")
    invalid();
  if (
    entry.greenInRegulation !== null &&
    typeof entry.greenInRegulation !== "boolean"
  )
    invalid();
  if (par === 3 && entry.fairwayHit !== null)
    throw new RoundCommandError(
      "Fairway hit is not applicable to a par-three hole.",
    );
  if (
    entry.status === "unset" &&
    (entry.putts !== null ||
      entry.fairwayHit !== null ||
      entry.greenInRegulation !== null)
  )
    invalid();
}

function sameNumbers(left: number[], right: number[]): boolean {
  return (
    Array.isArray(left) &&
    left.length === right.length &&
    left.every((number, index) => number === right[index])
  );
}
